import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';
import CircularProgress from '@mui/joy/CircularProgress';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import LinearProgress from '@mui/joy/LinearProgress';
import Alert from '@mui/joy/Alert';
import Sheet from '@mui/joy/Sheet';
import CreditCard from '@mui/icons-material/CreditCard';
import TrendingUp from '@mui/icons-material/TrendingUp';
import Warning from '@mui/icons-material/Warning';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Bolt from '@mui/icons-material/Bolt';
import { GET_MY_SUBSCRIPTION, GET_CLIENT/* , GET_TIERS */ } from '../../gql/subscription.queries';
import {
  CREATE_PORTAL_SESSION,
  CANCEL_SUBSCRIPTION,
  REACTIVATE_SUBSCRIPTION,
  CANCEL_PENDING_TIER_CHANGE
} from '../../gql/subscription.mutations';
import { useFeedback } from '../feedback/Service';
import BuyCreditsModal from './BuyCreditsModal';
import { getSubscriptionStatusLabel as getStatusLabel } from '../../utils';

export default function BillingSettings() {
  const { snackbar } = useFeedback();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);

  const { data: subscriptionData, loading: subLoading, refetch: refetchSubscription } = useQuery(GET_MY_SUBSCRIPTION);
  const { data: clientData, loading: clientLoading, refetch: refetchClient } = useQuery(GET_CLIENT);
  // const { data: tiersData } = useQuery(GET_TIERS);

  const [createPortalSession, { loading: portalLoading }] = useMutation(CREATE_PORTAL_SESSION);
  const [cancelSubscription, { loading: cancelLoading }] = useMutation(CANCEL_SUBSCRIPTION);
  const [reactivateSubscription, { loading: reactivateLoading }] = useMutation(REACTIVATE_SUBSCRIPTION);
  const [cancelPendingTierChange, { loading: cancelDowngradeLoading }] = useMutation(CANCEL_PENDING_TIER_CHANGE);

  const subscription = subscriptionData?.mySubscription;
  const client = clientData?.getClient;
  // const tiers = tiersData?.tiers || [];
  const isLoading = subLoading || clientLoading;
  const isMutating = portalLoading || cancelLoading || reactivateLoading || cancelDowngradeLoading;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return 0;
    const diff = new Date(dateString) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trialing': return 'primary';
      case 'past_due': return 'warning';
      case 'canceled': return 'danger';
      case 'free': return 'neutral';
      default: return 'neutral';
    }
  };

  const handleManageBilling = async () => {
    try {
      const { data } = await createPortalSession();
      if (data?.createPortalSession?.url) {
        window.location.href = data.createPortalSession.url;
      }
    } catch (error) {
      snackbar({ text: error.message || 'Failed to open billing portal', type: 'error' });
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription();
      await Promise.all([refetchSubscription(), refetchClient()]);
      setShowCancelModal(false);
      snackbar({ text: 'Subscription will be canceled at the end of the billing period', type: 'success' });
    } catch (error) {
      snackbar({ text: error.message || 'Failed to cancel subscription', type: 'error' });
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await reactivateSubscription();
      await Promise.all([refetchSubscription(), refetchClient()]);
      snackbar({ text: 'Subscription reactivated successfully', type: 'success' });
    } catch (error) {
      snackbar({ text: error.message || 'Failed to reactivate subscription', type: 'error' });
    }
  };

  const handleCreditsSuccess = async () => {
    await Promise.all([refetchSubscription(), refetchClient()]);
    setShowBuyCreditsModal(false);
  };

  const handleCancelDowngrade = async () => {
    try {
      await cancelPendingTierChange();
      await refetchSubscription();
      snackbar({ text: 'Pending plan change has been cancelled', type: 'success' });
    } catch (error) {
      snackbar({ text: error.message || 'Failed to cancel plan change', type: 'error' });
    }
  };

  const creditsPercentage = client?.totalCredits
    ? (client.periodCreditsUsed / client.totalCredits) * 100
    : 0;

  const isPaidPlan = subscription?.status && subscription.status !== 'free';
  const isCanceled = subscription?.cancelAtPeriodEnd;
  const hasPendingDowngrade = !!subscription?.pendingTierId && !isCanceled;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography level="h2" sx={{ fontWeight: 700 }}>
          Billing & Subscription
        </Typography>
        <Typography level="body-md" color="neutral">
          Manage your subscription, credits, and payment methods
        </Typography>
      </Box>

      {/* Payment Failure Alert */}
      {client?.creditsExpireAt && (
        <Alert
          color="danger"
          variant="soft"
          startDecorator={<Warning />}
          sx={{ mb: 3 }}
          endDecorator={
            <Button
              size="sm"
              variant="solid"
              color="danger"
              onClick={handleManageBilling}
              loading={portalLoading}
            >
              Update Payment
            </Button>
          }
        >
          <Box>
            <Typography level="title-sm" color="danger">
              Payment Failed
            </Typography>
            <Typography level="body-sm">
              Update your payment method within {getDaysUntil(client.creditsExpireAt)} days or lose your credits.
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Current Plan Card */}
      <Card sx={{ mb: 3, borderRadius: 'xl', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Sheet
                variant="soft"
                color="primary"
                sx={{ p: 1, borderRadius: 'md', display: 'flex' }}
              >
                <Bolt sx={{ fontSize: 24 }} />
              </Sheet>
              <Box>
                <Typography level="body-sm" color="neutral">
                  Current Plan
                </Typography>
                <Typography level="h3" sx={{ fontWeight: 700 }}>
                  {subscription?.tier?.name || client?.currentTier?.name || 'Free Plan'}
                </Typography>
              </Box>
            </Box>
            <Chip
              size="lg"
              variant="soft"
              color={getStatusColor(subscription?.status || 'free')}
              sx={{ fontWeight: 600 }}
            >
              {getStatusLabel(subscription?.status || 'free', subscription?.cancelAtPeriodEnd)}
            </Chip>
          </Box>

          {isCanceled && (
            <Alert color="warning" variant="soft" sx={{ mb: 3, borderRadius: 'md' }}>
              Your subscription will end on {formatDate(subscription?.currentPeriodEnd)}.
              You{"'"}ll be switched to the free plan after that and your credits will be burned down to the free plan limit.
            </Alert>
          )}

          {hasPendingDowngrade && (
            <Alert color="primary" variant="soft" sx={{ mb: 3, borderRadius: 'md' }}
              endDecorator={
                <Button
                  size="sm"
                  variant="solid"
                  color="primary"
                  onClick={handleCancelDowngrade}
                  loading={cancelDowngradeLoading}
                >
                  Cancel Downgrade
                </Button>
              }
            >
              <span>
                Your plan will change to <strong>{subscription.pendingTier?.name}</strong> on{' '}
                {formatDate(subscription.pendingTierChangeDate)}.
                You{"'"}ll continue on the current plan until then.
              </span>
            </Alert>
          )}

          {/* Billing Period Info */}
          {(subscription?.currentPeriodStart || subscription?.currentPeriodEnd) && (
            <Sheet
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 'md',
                mb: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}
            >
              {subscription?.currentPeriodStart && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarMonth sx={{ color: 'neutral.500', fontSize: 20 }} />
                  <Box>
                    <Typography level="body-xs" color="neutral">
                      Period Started
                    </Typography>
                    <Typography level="body-md" fontWeight="md">
                      {formatDate(subscription.currentPeriodStart)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {subscription?.currentPeriodEnd && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarMonth sx={{ color: 'neutral.500', fontSize: 20 }} />
                  <Box>
                    <Typography level="body-xs" color="neutral">
                      {isCanceled ? 'Access Until' : 'Next Billing'}
                    </Typography>
                    <Typography level="body-md" fontWeight="md">
                      {formatDate(subscription.currentPeriodEnd)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Sheet>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="soft"
              color="primary"
              startDecorator={<TrendingUp />}
              component={Link}
              to="/settings/billing/plan"
              sx={{ fontWeight: 600 }}
            >
              {isPaidPlan ? 'Change Plan' : 'Upgrade Plan'}
            </Button>

            {isPaidPlan && (
              <>
                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<CreditCard />}
                  onClick={handleManageBilling}
                  loading={portalLoading}
                >
                  Payment Method & Invoices
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Credits Card */}
      <Card sx={{ mb: 3, borderRadius: 'xl', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography level="title-lg" fontWeight="lg">
              Credits
            </Typography>
            <Button
              size="sm"
              variant="soft"
              onClick={() => setShowBuyCreditsModal(true)}
              disabled={!isPaidPlan}
            >
              Buy More Credits
            </Button>
          </Box>

          {/* Credits Display */}
          <Sheet
            variant="soft"
            color="primary"
            sx={{
              p: 3,
              borderRadius: 'lg',
              textAlign: 'center',
              mb: 3
            }}
          >
            <Typography level="h1" sx={{ fontWeight: 700, fontSize: '3rem', lineHeight: 1 }}>
              {client?.credits ?? 0}
            </Typography>
            <Typography level="body-sm" sx={{ mt: 1, opacity: 0.8 }}>
              credits available
            </Typography>
          </Sheet>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography level="body-sm" color="neutral">
                Usage
              </Typography>
              <Typography level="body-sm" color="neutral">
                {client?.periodCreditsUsed ?? 0} / {client?.totalCredits ?? 0}
              </Typography>
            </Box>
            <LinearProgress
              determinate
              value={Math.min(creditsPercentage, 100)}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography level="body-sm" color="neutral">
              <strong>{subscription?.tier?.monthlyCredits || client?.currentTier?.monthlyCredits || 0}</strong> credits added monthly
            </Typography>
            {!!subscription?.tier?.pricePerAdditionalCredit && (
              <Typography level="body-sm" color="neutral">
                Additional credits: <strong>${subscription.tier.pricePerAdditionalCredit}</strong> each
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Reactivate Subscription Card - shown when subscription is set to cancel */}
      {isPaidPlan && isCanceled && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 'xl',
            borderColor: 'success.300',
            mb: 3,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography level="title-lg" color="success" sx={{ mb: 2, fontWeight: 600 }}>
              Reactivate Your Subscription
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
              Changed your mind? Reactivate now to keep your plan and credits.
            </Typography>
            <Button
              variant="solid"
              color="success"
              onClick={handleReactivateSubscription}
              loading={reactivateLoading}
            >
              Reactivate Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Danger Zone - only shown when subscription is active (not canceled) */}
      {isPaidPlan && !isCanceled && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 'xl',
            borderColor: 'danger.300',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography level="title-lg" color="danger" sx={{ mb: 2, fontWeight: 600 }}>
              Danger Zone
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
              These actions can affect your subscription and access to features.
            </Typography>
            <Button
              variant="outlined"
              color="danger"
              onClick={() => setShowCancelModal(true)}
              disabled={isMutating}
            >
              Cancel Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal open={showCancelModal} onClose={() => setShowCancelModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Cancel Subscription?
          </Typography>
          <Typography level="body-md" sx={{ mb: 3 }}>
            Your subscription will remain active until{' '}
            <strong>{formatDate(subscription?.currentPeriodEnd)}</strong>.
            After that, you{"'"}ll be switched to the Free plan and may lose access to some features.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="plain" onClick={() => setShowCancelModal(false)}>
              Keep Subscription
            </Button>
            <Button
              color="danger"
              onClick={handleCancelSubscription}
              loading={cancelLoading}
            >
              Yes, Cancel
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        open={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        onSuccess={handleCreditsSuccess}
        pricePerCredit={subscription?.tier?.pricePerAdditionalCredit}
        currentCredits={client?.credits || 0}
        maxCredits={client?.currentTier?.maxCreditsAllowed || 0}
      />
    </Box>
  );
}
